import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "./api";

// Reload X when Y/Z is updated or deleted
const dependencies = {
  // Updates are made via the admin and member viewsets, which need to be reflected in the main user query
  user: ["users/members", "users/admins"],
  // users/members contains the total transactions, so anything that has a numeric impact needs to taken into account
  "users/members": [
    "jobs",
    "responsibilities",
    "performed-jobs",
    "performed-responsibilities",
    "submissions",
    "transactions",
  ],
  // performed jobs and responsibilities cannot exist with their jobs / responsibilities and the submission
  "performed-jobs": ["jobs", "submissions"],
  "performed-responsibilities": ["responsibilities", "submissions"],
  // submissions have a total which is driven by all the numerically important types
  submissions: [
    "jobs",
    "responsibilities",
    "performed-jobs",
    "performed-responsibilities",
  ],
  // transactions have a total which is driven by all the numerically important types
  transactions: [
    "jobs",
    "responsibilities",
    "performed-jobs",
    "performed-responsibilities",
    "submissions",
  ],
  invites: ["users/members", "users/admins"], // Deleting a member should invalidate the invites
};

function invalidate(queryClient, contentType) {
  queryClient.invalidateQueries({
    queryKey: [contentType],
  });
  Object.keys(dependencies).forEach((key) => {
    if (dependencies[key].includes(contentType)) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  });
}

export function usePostQuery(contentType, options = {}) {
  const queryClient = useQueryClient();
  const updateCache = options.updateCache ?? true;

  return useMutation({
    mutationFn: async (values) => {
      const { data } = await apiPost(contentType, values);
      return data;
    },
    onSuccess: () => {
      if (updateCache) {
        invalidate(queryClient, contentType);
      }
    },
  });
}

export function useGetQuery(contentType, options = {}) {
  const enabled = options.enabled ?? true;
  const identifier = options.identifier;
  const queryParams = options.queryParams || {};

  return useQuery({
    enabled,
    queryKey: [contentType, identifier, { ...queryParams }],
    queryFn: async () => {
      const url = identifier ? `${contentType}/${identifier}` : contentType;
      const { data } = await apiGet(url, queryParams);
      return data;
    },
  });
}

export function usePatchQuery(contentType, options = {}) {
  const queryClient = useQueryClient();
  const updateCache = options.updateCache ?? true;
  const identifier = options.identifier;

  return useMutation({
    mutationFn: async (values) => {
      const { data } = await apiPatch(contentType, identifier, values);
      return data;
    },
    onSuccess: () => {
      if (updateCache) {
        invalidate(queryClient, contentType);
      }
    },
  });
}

export function useDeleteQuery(contentType, options = {}) {
  const queryClient = useQueryClient();
  const updateCache = options.updateCache ?? true;

  return useMutation({
    mutationFn: async (identifier) => {
      await apiDelete(contentType, identifier);
    },
    onSuccess: () => {
      if (updateCache) {
        invalidate(queryClient, contentType);
      }
    },
  });
}
